import {
  AttributeDefinitions,
  AttributeName,
  AttributeSelect,
  CodingAttributes,
} from "@keita/lanepatrol-sdk";
import { OsaNodeCoding } from "../../coding/models/OsaNodeCoding";
import { OsaNode } from "../../osaway/models/OsaNode";
import { OsaWayApi } from "../../osaway/services/OsaWayApi";
import { downloadFile } from "../../shared/utils/file-utils";
import { OsaWay } from "../../osaway/models/OsaWay";

enum Col {
  coderName,
  codingDate,
  projectName,
  projectCity,
  routeName,
  photoDate,
  photoReference,
  photoName,
  distanceFromStart,
  distancetoNext,
  latitudeStart,
  longitudeStart,
  latitudeEnd,
  longitudeEnd,
  scoreGlobal,
  scoreVB,
  scoreBB,
  scoreBP,
  scoreSB,
  comment,
  areaType,
  facilityType,
  facilityAccess,
  looseOrSlipperySurface,
  tramOrTrainRails,
  majorSurfaceDeformationOrDrainOpening,
  fixedObstacleOnFacility,
  nonFixedObstacleOnFacility,
  lineOfSight,
  delineation,
  lightSegregation,
  facilityWidthPerDirection,
  flowDirection,
  widthRestriction,
  roadLane01,
  vehicleParking01,
  severeHazard01,
  objectOrLevelChange01,
  sidewalk01,
  roadLane13,
  vehicleParking13,
  severeHazard13,
  objectOrLevelChange13,
  sidewalk13,
  grade,
  curvature,
  streetLighting,
  pedestrianCrossing,
  intersectionBicycleFacility,
  intersectionApproach,
  intersectionOrRoadCrossing,
  crossingFacility,
  numberOfLanesAdjacentRoad,
  numberOfLanesIntersectingRoad,
  propertyAccess,
  peakPedestrianFlowAlongOrAcrossFacility,
  peakBicycleAndLVTrafficFlow,
  observedProportionOfCargoBikesAndMopeds,
  bicycleAndLVSpeedAverage,
  bicycleAndLVSpeedDifferential,
  roadAADT,
  heavyVehicleFlow,
  roadSpeedLimit,
  roadOperatingSpeed,
}

type osaWayId = string;
type nodeId = string;

export const codingExport = async (
  osaWayIds: string[],
  overwriteValues?: {
    allNodeCodingsByWayId: Map<osaWayId, Map<nodeId, OsaNodeCoding>>;
    osaNodes: Map<osaWayId, OsaNode[]>;
    osaWays: OsaWay[];
  },
) => {
  var finalCsvText = "";

  for (const osaWayId of osaWayIds) {
    const { data: blob } = await OsaWayApi.getCodingCsv(osaWayId);
    const csvText = await blobToText(blob);

    const csvLines = csvText.split("\n");
    const headerLine = csvLines[0];
    const dataLines = csvLines.slice(1);

    if (overwriteValues) {
      const roadCodingEntries =
        overwriteValues.allNodeCodingsByWayId.get(osaWayId)!;
      const nodes = overwriteValues.osaNodes.get(osaWayId)!;

      dataLines.forEach((lineStr, index) => {
        const line = lineStr.trim();
        if (line) {
          const node = nodes[index]!;
          dataLines[index] = overwriteLine(
            line,
            roadCodingEntries.get(node.id)!.attributes,
            nodes[index],
          );
        }
      });
    }

    if (finalCsvText) {
      finalCsvText += dataLines.join("\n");
    } else {
      finalCsvText += [headerLine, ...dataLines].join("\n");
    }
  }

  downloadFile(new Blob([finalCsvText]), `Coding_${new Date().getTime()}.csv`);
};

const overwriteLine = (
  line: string,
  attributes: CodingAttributes,
  node: OsaNode,
): string => {
  const values = line.split(",");

  values[Col.coderName] = "";
  values[Col.codingDate] = "";

  if (node.score) {
    values[Col.scoreGlobal] = node.score.global.value.toFixed(2);
    values[Col.scoreBB] = node.score.bicycleToBicycle.value.toFixed(2);
    values[Col.scoreBP] = node.score!.bicycleToPedestrian.value.toFixed(2);
    values[Col.scoreSB] = node.score.singleBicycle.value.toFixed(2);
    values[Col.scoreVB] = node.score.vehicleToBicycle.value.toFixed(2);
  }

  values[Col.areaType] = getOptionIndex(
    AttributeName.AreaType,
    attributes.areaType,
  );
  values[Col.facilityType] = getOptionIndex(
    AttributeName.FacilityType,
    attributes.facilityType,
  );
  values[Col.facilityAccess] = getOptionIndex(
    AttributeName.FacilityAccess,
    attributes.facilityAccess,
  );
  values[Col.looseOrSlipperySurface] = getOptionIndex(
    AttributeName.LooseOrSlipperySurface,
    attributes.looseOrSlipperySurface,
  );
  values[Col.tramOrTrainRails] = getOptionIndex(
    AttributeName.TramOrTrainRails,
    attributes.tramOrTrainRails,
  );
  values[Col.majorSurfaceDeformationOrDrainOpening] = getOptionIndex(
    AttributeName.MajorSurfaceDeformationOrDrainOpening,
    attributes.majorSurfaceDeformationOrDrainOpening,
  );
  values[Col.fixedObstacleOnFacility] = getOptionIndex(
    AttributeName.FixedObstacleOnFacility,
    attributes.fixedObstacleOnFacility,
  );
  values[Col.nonFixedObstacleOnFacility] = getOptionIndex(
    AttributeName.NonFixedObstacleOnFacility,
    attributes.nonFixedObstacleOnFacility,
  );
  values[Col.lineOfSight] = getOptionIndex(
    AttributeName.LineOfSight,
    attributes.lineOfSight,
  );
  values[Col.delineation] = getOptionIndex(
    AttributeName.Delineation,
    attributes.delineation,
  );
  values[Col.lightSegregation] = getOptionIndex(
    AttributeName.LightSegregation,
    attributes.lightSegregation,
  );
  values[Col.facilityWidthPerDirection] = getOptionIndex(
    AttributeName.FacilityWidthPerDirection,
    attributes.facilityWidthPerDirection,
  );
  values[Col.flowDirection] = getOptionIndex(
    AttributeName.FlowDirection,
    attributes.flowDirection,
  );
  values[Col.widthRestriction] = getOptionIndex(
    AttributeName.WidthRestriction,
    attributes.widthRestriction,
  );
  values[Col.roadLane01] = getOptionIndex(
    AttributeName.AdjacentFeatures0to1mRoadLane,
    attributes.adjacentFeatures0to1m?.roadLane ?? null,
  );
  values[Col.vehicleParking01] = getOptionIndex(
    AttributeName.AdjacentFeatures0to1mVehicleParking,
    attributes.adjacentFeatures0to1m?.vehicleParking ?? null,
  );
  values[Col.severeHazard01] = getOptionIndex(
    AttributeName.AdjacentFeatures0to1mSevereHazard,
    attributes.adjacentFeatures0to1m?.severeHazard ?? null,
  );
  values[Col.objectOrLevelChange01] = getOptionIndex(
    AttributeName.AdjacentFeatures0to1mObjectOrLevelChange,
    attributes.adjacentFeatures0to1m?.objectOrLevelChange ?? null,
  );
  values[Col.sidewalk01] = getOptionIndex(
    AttributeName.AdjacentFeatures0to1mSidewalk,
    attributes.adjacentFeatures0to1m?.sidewalk ?? null,
  );
  values[Col.roadLane13] = getOptionIndex(
    AttributeName.AdjacentFeatures1to3mRoadLane,
    attributes.adjacentFeatures1to3m?.roadLane ?? null,
  );
  values[Col.vehicleParking13] = getOptionIndex(
    AttributeName.AdjacentFeatures1to3mVehicleParking,
    attributes.adjacentFeatures1to3m?.vehicleParking ?? null,
  );
  values[Col.severeHazard13] = getOptionIndex(
    AttributeName.AdjacentFeatures1to3mSevereHazard,
    attributes.adjacentFeatures1to3m?.severeHazard ?? null,
  );
  values[Col.objectOrLevelChange13] = getOptionIndex(
    AttributeName.AdjacentFeatures1to3mObjectOrLevelChange,
    attributes.adjacentFeatures1to3m?.objectOrLevelChange ?? null,
  );
  values[Col.sidewalk13] = getOptionIndex(
    AttributeName.AdjacentFeatures1to3mSidewalk,
    attributes.adjacentFeatures1to3m?.sidewalk ?? null,
  );
  values[Col.grade] = getOptionIndex(AttributeName.Grade, attributes.grade);
  values[Col.curvature] = getOptionIndex(
    AttributeName.Curvature,
    attributes.curvature,
  );
  values[Col.streetLighting] = getOptionIndex(
    AttributeName.StreetLighting,
    attributes.streetLighting,
  );
  values[Col.pedestrianCrossing] = getOptionIndex(
    AttributeName.PedestrianCrossing,
    attributes.pedestrianCrossing,
  );
  values[Col.intersectionBicycleFacility] = getOptionIndex(
    AttributeName.IntersectionBicycleFacility,
    attributes.intersectionBicycleFacility,
  );
  values[Col.intersectionApproach] = getOptionIndex(
    AttributeName.IntersectionApproach,
    attributes.intersectionApproach,
  );
  values[Col.intersectionOrRoadCrossing] = getOptionIndex(
    AttributeName.IntersectionOrRoadCrossing,
    attributes.intersectionOrRoadCrossing,
  );
  values[Col.crossingFacility] = getOptionIndex(
    AttributeName.CrossingFacility,
    attributes.crossingFacility,
  );
  values[Col.numberOfLanesAdjacentRoad] = getOptionIndex(
    AttributeName.NumberOfLanesAdjacentRoad,
    attributes.numberOfLanesAdjacentRoad,
  );
  values[Col.numberOfLanesIntersectingRoad] = getOptionIndex(
    AttributeName.NumberOfLanesIntersectingRoad,
    attributes.numberOfLanesIntersectingRoad,
  );
  values[Col.propertyAccess] = getOptionIndex(
    AttributeName.PropertyAccess,
    attributes.propertyAccess,
  );
  values[Col.peakPedestrianFlowAlongOrAcrossFacility] = getOptionIndex(
    AttributeName.PeakPedestrianFlowAlongOrAcrossFacility,
    attributes.peakPedestrianFlowAlongOrAcrossFacility,
  );
  values[Col.peakBicycleAndLVTrafficFlow] = getOptionIndex(
    AttributeName.PeakBicycleAndLVTrafficFlow,
    attributes.peakBicycleAndLVTrafficFlow,
  );
  values[Col.observedProportionOfCargoBikesAndMopeds] = getOptionIndex(
    AttributeName.ObservedProportionOfCargoBikesAndMopeds,
    attributes.observedProportionOfCargoBikesAndMopeds,
  );
  values[Col.bicycleAndLVSpeedAverage] = getOptionIndex(
    AttributeName.BicycleAndLVSpeedAverage,
    attributes.bicycleAndLVSpeedAverage,
  );
  values[Col.bicycleAndLVSpeedDifferential] = getOptionIndex(
    AttributeName.BicycleAndLVSpeedDifferential,
    attributes.bicycleAndLVSpeedDifferential,
  );
  values[Col.roadAADT] = getOptionIndex(
    AttributeName.RoadAADT,
    attributes.roadAADT,
  );
  values[Col.heavyVehicleFlow] = getOptionIndex(
    AttributeName.HeavyVehicleFlow,
    attributes.heavyVehicleFlow,
  );
  values[Col.roadSpeedLimit] = attributes.roadSpeedLimit?.toString() ?? "";
  values[Col.roadOperatingSpeed] =
    attributes.roadOperatingSpeed?.toString() ?? "";

  return values.join(",");
};

const getOptionIndex = (
  attributeName: AttributeName,
  value: string | null,
): string => {
  if (value === null) return "";
  const attribute = AttributeDefinitions.getByName(
    attributeName,
  ) as AttributeSelect;
  return (
    attribute.options.findIndex((option) => option.value === value) + 1
  ).toString();
};

const blobToText = async (blob: Blob) => {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      resolve(reader.result?.toString() ?? "");
    };
    reader.onerror = reject;
    reader.readAsText(blob);
  });
};
